import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, DollarSign, Loader2, Package } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export default function SellPayouts() {
  const [status, setStatus] = useState(null);
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState({ sales: [], total_payout_cents: 0, count: 0 });
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", price: "", image: "" });

  const refresh = useCallback(() => {
    api.get("/connect/status").then(({ data }) => setStatus(data)).catch(() => {});
    api.get("/connect/products", { params: { owner: "me" } }).then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/me/sales").then(({ data }) => setSales(data)).catch(() => {});
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const onboard = async () => {
    try {
      setBusy(true);
      const { data } = await api.post("/connect/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start onboarding.");
      setBusy(false);
    }
  };

  const createProduct = async () => {
    if (!form.name || !form.price) { toast.error("Name and price are required."); return; }
    try {
      setBusy(true);
      await api.post("/connect/products", {
        name: form.name, description: form.description,
        price: parseFloat(form.price), image: form.image,
      });
      toast.success("Product created.");
      setForm({ name: "", description: "", price: "", image: "" });
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create product.");
    } finally { setBusy(false); }
  };

  const ready = status?.ready_to_receive_payments;
  const fee = status?.fee_pct ?? 30;

  return (
    <div data-testid="dash-sell">
      <h1 className="font-display text-3xl font-bold">Sell & Payouts</h1>
      <p className="mt-2 text-sm text-neutral-400">You keep {100 - fee}% of every sale. Barbistry's {fee}% fee is taken automatically at checkout — you're paid instantly, never waiting on anyone.</p>

      {/* payout status (read live from the Stripe Accounts API) */}
      <div className="mt-6 bg-[#111] border border-white/10 p-6">
        {!status ? (
          <Loader2 className="animate-spin text-[#D4AF37]" />
        ) : ready ? (
          <div className="flex items-center gap-3 text-[#D4AF37]" data-testid="payout-ready">
            <CheckCircle2 /> <div><p className="font-semibold">Payouts active</p><p className="text-xs text-neutral-400">Your connected account can accept payments and receive payouts.</p></div>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3 text-neutral-300">
              <AlertCircle className="text-[#D4AF37]" />
              <div>
                <p className="font-semibold">{status.connected ? (status.onboarding_complete ? "Verifying your account" : "Finish payout setup") : "Set up payouts"}</p>
                <p className="text-xs text-neutral-400">Onboard a connected account (Stripe Express) to receive money.</p>
              </div>
            </div>
            <Button onClick={onboard} disabled={busy} data-testid="connect-onboard-btn" className="rounded-none bg-[#D4AF37] text-black hover:bg-[#F0C84B] font-semibold">
              {busy ? "…" : status.connected ? "Continue setup" : "Onboard to collect payments"}
            </Button>
          </div>
        )}
      </div>

      {/* earnings */}
      <div className="mt-4 grid grid-cols-2 gap-4">
        <div className="bg-[#111] border border-white/10 p-5">
          <DollarSign size={18} className="text-[#D4AF37]" />
          <p className="mt-2 text-2xl font-display font-bold">${(sales.total_payout_cents / 100).toFixed(2)}</p>
          <p className="text-xs text-neutral-500">Total payouts earned</p>
        </div>
        <div className="bg-[#111] border border-white/10 p-5">
          <p className="text-2xl font-display font-bold">{sales.count}</p>
          <p className="text-xs text-neutral-500">Sales</p>
        </div>
      </div>

      {/* create product */}
      <div className="mt-8 bg-[#0a0a0a] border border-white/10 p-6">
        <h2 className="font-display text-xl font-semibold">Create a product</h2>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label className="text-neutral-300">Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name" className="mt-1.5 bg-[#111] border-white/10 rounded-none focus-visible:ring-[#D4AF37]" />
          </div>
          <div>
            <Label className="text-neutral-300">Price (USD)</Label>
            <Input type="number" min="1" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="product-price" className="mt-1.5 bg-[#111] border-white/10 rounded-none focus-visible:ring-[#D4AF37]" />
          </div>
          <div>
            <Label className="text-neutral-300">Image URL</Label>
            <Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} data-testid="product-image" className="mt-1.5 bg-[#111] border-white/10 rounded-none focus-visible:ring-[#D4AF37]" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-neutral-300">Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="product-desc" className="mt-1.5 bg-[#111] border-white/10 rounded-none focus-visible:ring-[#D4AF37]" />
          </div>
        </div>
        <Button onClick={createProduct} disabled={busy} data-testid="product-create" className="mt-5 rounded-none bg-[#D4AF37] text-black hover:bg-[#F0C84B] font-semibold px-8">Create product</Button>
        {!ready && <p className="mt-3 text-xs text-neutral-500">You can create products now, but buyers can only pay once your payouts are active.</p>}
      </div>

      {/* my products */}
      {products.length > 0 && (
        <div className="mt-8">
          <h2 className="font-display text-xl font-semibold">Your products</h2>
          <div className="mt-4 space-y-2">
            {products.map((p) => (
              <div key={p.id} className="flex items-center gap-4 bg-[#111] border border-white/10 p-4" data-testid={`my-product-${p.id}`}>
                {p.image ? <img src={p.image} alt="" className="w-12 h-12 object-cover" /> : <div className="w-12 h-12 bg-[#0a0a0a] flex items-center justify-center"><Package size={18} className="text-neutral-600" /></div>}
                <div className="flex-1">
                  <p className="font-semibold">{p.name}</p>
                  <p className="text-sm text-[#D4AF37]">${(p.price_cents / 100).toFixed(2)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
